import moment from 'moment';
import { get } from 'lodash';
import { formatAmountForLocale } from 'Utils/currency-config-for-locale/currencyConfigForLocale';
import { formatApiDate, timestampToApiDate } from 'Utils/date/apiDate';
const API_DATE_FORMAT = 'YYYYMMDD';
export type ChildrenProps = ReadonlyArray<{
  readonly id: string;
  readonly birthdate: string;
  readonly customerId?: string;
  readonly firstName?: string;
  readonly lastName?: string;
}>;
export type ChildcareType = Readonly<UpsellChildcare['productInfo']>;

function getCtaTypeChildcare(childcare: UpsellChildcare): {
  type: 'add' | 'disabled' | 'none' | 'remove' | 'full';
  tracking?: 'addToCart' | 'removeFromCart';
} {
  const { childId } = childcare.pricingInfo;
  const currentChildData = childcare.pricingInfo.attendees?.find(
    (attendee) => attendee.id === childId,
  );
  const currentChildHasRentedChildcare =
    currentChildData?.rentedAmount && currentChildData.rentedAmount.value > 0;

  if (currentChildHasRentedChildcare) {
    return {
      type: 'remove',
      tracking: 'removeFromCart',
    };
  }

  const currentChildHasPaidChildcare = currentChildData?.schedules?.some(
    ({ isPartiallyPaid, isFullyPaid }) => isPartiallyPaid || isFullyPaid,
  );

  if (
    currentChildHasPaidChildcare ||
    childcare.pricingInfo.isPartiallyPaid ||
    childcare.pricingInfo.isFullyPaid
  ) {
    return {
      type: 'none',
    };
  }

  const currentChildInfos = childcare.pricingInfo.attendees?.find(
    (attendee) => attendee.id === childId,
  );
  // the mini clubmed is always included
  const isChildcarePetitOrBaby = childcare.productInfo.ageInMonths.min === 4 || 24;
  const allScheduleSlotsAreFullOrPaid = currentChildInfos?.schedules?.every(({ timeSlots }) =>
    timeSlots.every(({ remainingStock, isPaid }) => remainingStock === 0 || isPaid),
  );
  const childcareIsFull = isChildcarePetitOrBaby && allScheduleSlotsAreFullOrPaid;

  if (childcareIsFull) {
    return {
      type: 'full',
    };
  }

  return {
    type: 'add',
    tracking: 'addToCart',
  };
}

function getFormattedPricePerDay(price: number, tripDuration: number, locale: string) {
  const pricePerDay = price / tripDuration;
  return formatAmountForLocale(pricePerDay, locale);
}

function isChildAgedXOrLess(birthdate: string, targetAge: number) {
  return moment(birthdate, API_DATE_FORMAT).isAfter(
    moment().startOf('day').subtract(targetAge, 'years'),
  );
}

function hasChildrenAgedXOrLess(
  attendees: ReadonlyArray<{
    readonly birthdate: string;
  }>,
  targetAge: number,
) {
  return (
    attendees.filter((attendee) => isChildAgedXOrLess(attendee.birthdate, targetAge)).length > 0
  );
}

function getCurrentChild(items: ChildrenProps, itemId: string) {
  return items.find(({ id }) => id === itemId);
}

function getCurrentKid(items: Array<any>, currentChild: any): Kid {
  return items.find((item) => {
    if (item.id) {
      return (
        timestampToApiDate(item.birthdate) === currentChild.birthdate && item.id === currentChild.id
      );
    }

    if (item.neolid && currentChild.customerId) {
      return (
        timestampToApiDate(item.birthdate) === currentChild.birthdate &&
        item.neolid === currentChild.customerId
      );
    }

    return timestampToApiDate(item.birthdate) === currentChild.birthdate;
  });
}

function getFormatDate(item: any, message: string) {
  return formatApiDate(get(item, 'birthdate', ''), message);
}

const includedAndNotIncludedChildcares = (childcareList: ReadonlyArray<UpsellChildcare>) =>
  childcareList.reduce(
    (acc, childcare) => {
      if (childcare.productInfo.isIncluded) {
        acc.includedChildcares.push(childcare);
      } else {
        acc.notIncludedChildcares.push(childcare);
      }

      return acc;
    },
    {
      includedChildcares: [],
      notIncludedChildcares: [],
    },
  );

function getRentedTimeSlotId(
  pricingInfo: UpsellChildcare['pricingInfo'],
  childId: string | undefined,
): string | undefined {
  const attendee = pricingInfo.attendees?.find(({ id }) => id === childId);
  const rentedTimeSlot = attendee?.schedules
    ?.flatMap(({ timeSlots }) => timeSlots || [])
    .find(({ isRented, isInCart, isPaid }) => isRented || isInCart || isPaid);

  return rentedTimeSlot?.id;
}

function getChildcareServiceId(childcare: UpsellChildcare): string {
  const { childId, progId, schedules } = childcare.pricingInfo;
  const rentedTimeSlotId = getRentedTimeSlotId(childcare.pricingInfo, childId);

  if (rentedTimeSlotId) {
    return rentedTimeSlotId;
  }

  const slots = schedules?.flatMap(({ slots }) => slots || []) ?? [];
  const rentedSlot = slots.find(({ attendees }) =>
    attendees?.some(
      (attendee) =>
        attendee.id === childId && (attendee.isRented || attendee.isInCart || attendee.isPaid),
    ),
  );

  if (rentedSlot?.id) {
    return rentedSlot.id;
  }

  if (slots.length === 1) {
    return slots[0].id;
  }

  return progId || slots[0]?.id || '';
}

function getChildcareRemovePriceLabel(
  childcare: UpsellChildcare,
  childId: string | undefined,
): string | undefined {
  const attendee = childcare.pricingInfo.attendees?.find(({ id }) => id === childId);

  if (!attendee) {
    return undefined;
  }

  const activeTimeSlots =
    attendee.schedules
      ?.flatMap(({ timeSlots }) => timeSlots || [])
      .filter(({ isRented, isPaid, isInCart }) => isRented || isPaid || isInCart) || [];

  if (activeTimeSlots.length === 1 && activeTimeSlots[0].price?.value > 0) {
    return activeTimeSlots[0].price.formattedValue;
  }

  if (attendee.rentedAmount?.value > 0) {
    return attendee.rentedAmount.formattedValue;
  }

  return undefined;
}

const getOverloadChildcareDataForChalet = ({
  proposals,
  currentChildcareId,
  currentChildId,
}: {
  proposals: ReadonlyArray<Proposal>;
  currentChildcareId: string;
  currentChildId: string;
}) => {
  if (
    proposals.every(({ additionalServices }) =>
      additionalServices.childcares.some(({ pricingInfo }) =>
        pricingInfo.schedules.some(({ slots }) => {
          const r = slots.some(
            ({ attendees, id }) =>
              id === currentChildcareId &&
              attendees.some(({ id: idChild }) => idChild === currentChildId),
          );
          return r;
        }),
      ),
    )
  ) {
    return;
  }

  const proposalsWithDifferentChildcare = proposals.filter(
    ({ additionalServices }) =>
      !additionalServices.childcares.some(({ pricingInfo }) =>
        pricingInfo.schedules.some(({ slots }) => {
          const r = slots.some(
            ({ attendees, id }) =>
              id === currentChildcareId &&
              attendees.some(({ id: idChild }) => idChild === currentChildId),
          );
          return r;
        }),
      ),
  );
  const childcareForOverload =
    proposalsWithDifferentChildcare[0].additionalServices.childcares.find(({ pricingInfo }) =>
      pricingInfo.attendees?.some(({ id }) => id === currentChildId),
    );

  const payload = {
    proposalIds: proposalsWithDifferentChildcare.map<string>(({ id }) => id),
    serviceIds: [childcareForOverload ? getChildcareServiceId(childcareForOverload) : ''],
  };
  return payload;
};

export {
  getChildcareServiceId,
  getRentedTimeSlotId,
  getChildcareRemovePriceLabel,
  getCtaTypeChildcare,
  includedAndNotIncludedChildcares,
  getFormattedPricePerDay,
  hasChildrenAgedXOrLess,
  getCurrentChild,
  getCurrentKid,
  getFormatDate,
  getOverloadChildcareDataForChalet,
};
